import quickviewOpenSchema from './schemas/quickviewOpen';
import swatchClickSchema from './schemas/swatchClick';
import addToBagSchema from './schemas/addToCart';
import soldOutSizeTileSchema from './schemas/soldOutSizeTileSchema';
import universalClickSchema from './schemas/universalClick';
import productNotificationSuccessSchema from './schemas/productNotificationSuccess';

const badgesToCsvString = (badges) => badges?.map((badge) => badge?.analyticsLabel ?? badge?.label?.toLowerCase())?.join(', ');
const parseBreadcrumbsToCategoryName = (breadcrumbsUrls) => breadcrumbsUrls?.map((url) => url.enName).join('>');
const priceToDigits = (price) => price?.replace(/[^0-9 .,]/g, '');
const getProductMessage = (showUrgencyMessage) => (showUrgencyMessage ? 'almost gone' : 'coverage');

function oldSwatchChangePdp(productPageState) {
  window.digitalData.trigger('swatch_click', {
    event_name: 'swatch_click',
    brand: productPageState.collectionData?.brand,
    product_rating: productPageState?.reviewData?.averageOverallRating || 'null',
    category_id: productPageState.categoryId,
    collection_id: productPageState.collectionId,
    key_item_color: productPageState.collectionData?.activeProduct?.kicId,
    long_sku: productPageState.collectionData?.activeProduct?.storeItemNumber,
    short_sku: productPageState.collectionData?.qualifiedSku,
    price_flag: productPageState.collectionData?.activeProduct?.priceFlag,
    product_id: productPageState.collectionData?.activeProduct?.productId,
    product_name: productPageState.collectionData.activeProduct.productName,
    swatch_color: productPageState.collectionData.activeProduct.defaultSwatchSequence,
    product_badge: badgesToCsvString(productPageState.collectionData.activeProduct.badges),
    price_list_local: priceToDigits(productPageState.collectionData?.activeProduct?.prices.list.originalPrice),
    price_offer_local: priceToDigits(productPageState.collectionData?.activeProduct?.prices.list.discountPrice),
    category_name: parseBreadcrumbsToCategoryName(productPageState.collectionData?.breadcrumbsUrls),
    inventory: `${getProductMessage(productPageState.collectionData?.showUrgencyMessage)}:${productPageState.collectionData?.activeProduct?.analyticsSwatchAvailability}`,
    product_group: productPageState.collectionData?.activeProduct?.productGroupType || 'null',
  });
}

function oldUniversalClick(dataAction, dataText) {
  if (typeof window === 'undefined') return;
  window.digitalData.trigger('universal_click', {
    event_name: 'click',
    tealium_event: 'click',
    data_action: dataAction,
    data_text: dataText,
  });
}

function mapBaseProductDataToTealiumVariables(productPageState = {}, additionalData = {}) {
  const { collectionData = {}, reviewData = {}, collectionId } = productPageState;
  const {
    activeProduct = {}, breadcrumbsUrls = [], brand = 'null', primaryCategoryId, showUrgencyMessage, qualifiedSku,
  } = collectionData;

  return {
    brand,
    category_id: `${primaryCategoryId}` || 'null',
    category_name: parseBreadcrumbsToCategoryName(breadcrumbsUrls) || 'null',
    collection_id: collectionId || 'null',
    inventory: `${getProductMessage(showUrgencyMessage)}:${activeProduct?.analyticsSwatchAvailability}` || 'null',
    key_item_color: activeProduct?.kicId || 'null',
    long_sku: activeProduct?.storeItemNumber || 'null',
    parent_category_id: breadcrumbsUrls?.[1]?.categoryId || 'null',
    price_flag: activeProduct?.priceFlag || 'null',
    price_list_local: priceToDigits(activeProduct?.prices?.list?.originalPrice) || 'null',
    price_list_usd: activeProduct?.prices?.list?.listPriceUSD || 'null',
    price_offer_local: priceToDigits(activeProduct?.prices?.list?.discountPrice) || 'null',
    price_offer_usd: activeProduct?.prices?.list?.offerPriceUSD || 'null',
    product_badge: badgesToCsvString(activeProduct.badges) || 'null',
    product_id: activeProduct?.productId || 'null',
    product_name: activeProduct?.productName || 'null',
    product_rating: reviewData?.averageOverallRating || 'null',
    quantity: additionalData?.selectedQuantity?.toString() || '1',
    short_descriptor: activeProduct?.shortDescription || 'null',
    short_sku: qualifiedSku || 'null',
    swatch_color: activeProduct?.defaultSwatchSequence || 'null',
    product_view_method: productPageState.productViewMethod || 'null',
    page_view_method: productPageState.pageViewMethod || 'null',
    page_finding_method: productPageState.pageFindingMethod || 'null',
    data_text: productPageState.dataText || 'null',
    data_action: productPageState.dataAction || 'null',
    product_group: activeProduct?.productGroupType || 'null',
  };
}

export default class AnalyticsTriggers {
  static quickviewOpen(productPageState) {
    if (typeof window !== 'undefined') {
      window.analytics.triggerEvent({
        event_name: 'quick_view',
        ...mapBaseProductDataToTealiumVariables(productPageState),
      }, quickviewOpenSchema);
    }
  }

  static productDetail(productPageState) {
    if (typeof window === 'undefined') return;

    if (window.digitalData?.get('flag.pdp-pageload-event-from-mfe', false)) {
      const triggerPageLoadEvent = () => {
        window.removeEventListener('crs-application-initialized', triggerPageLoadEvent);

        window.digitalData.trigger('analytics.product_detail', {
          event_name: 'product_detail',
          page_description: 'product',
          brand: productPageState.collectionData?.brand,
          product_rating: productPageState?.reviewData?.averageOverallRating || 'null',
          category_id: productPageState.categoryId,
          collection_id: productPageState.collectionId,
          key_item_color: productPageState.collectionData?.activeProduct?.kicId,
          long_sku: productPageState.collectionData?.activeProduct?.storeItemNumber,
          short_sku: productPageState.collectionData?.qualifiedSku,
          price_flag: productPageState.collectionData?.activeProduct?.priceFlag,
          product_id: productPageState.collectionData?.activeProduct?.productId,
          product_name: productPageState.collectionData.activeProduct.productName,
          swatch_color: productPageState.collectionData.activeProduct.defaultSwatchSequence,
          product_badge: badgesToCsvString(productPageState.collectionData.activeProduct.badges),
          price_list_local: priceToDigits(productPageState.collectionData?.activeProduct?.prices.list.originalPrice),
          price_offer_local: priceToDigits(productPageState.collectionData?.activeProduct?.prices.list.discountPrice),
          category_name: parseBreadcrumbsToCategoryName(productPageState.collectionData?.breadcrumbsUrls),
          pdp_inventory: `${getProductMessage(productPageState.collectionData?.showUrgencyMessage)}:${productPageState.collectionData?.activeProduct?.analyticsSwatchAvailability}`,
        });
      };
      window.addEventListener('crs-application-initialized', triggerPageLoadEvent);
    }
  }

  static swatchClick(productPageState, hasPdpNewSwatchClickAnalytics) {
    if (typeof window === 'undefined') return;

    const isNewSwatchAnalyticsEnabled = productPageState.isQuickview || hasPdpNewSwatchClickAnalytics;

    if (isNewSwatchAnalyticsEnabled) {
      window.analytics.triggerEvent({
        event_name: 'swatch_click',
        ...mapBaseProductDataToTealiumVariables(productPageState),
      }, swatchClickSchema);
    } else {
      oldSwatchChangePdp(productPageState);
    }
  }

  static addToBag(productPageState, data) {
    if (typeof window === 'undefined') return;

    const hasGoldenStateAnalytics = productPageState.isQuickview || data.hasGoldenStateAnalytics;

    if (hasGoldenStateAnalytics) {
      window.analytics.triggerEvent({
        event_name: 'add_to_cart',
        ...mapBaseProductDataToTealiumVariables(productPageState, data),
        price_list_usd: data?.atbResponseData?.listPriceUSD?.toString() || 'null',
        price_offer_usd: data?.atbResponseData?.offerPriceUSD?.toString() || 'null',
      }, addToBagSchema);
    } else {
      window.digitalData.trigger('add_to_cart', {
        sticky_atb: window.digitalData.get('addToBag.stickyAtb', false),
        event_name: 'add_to_cart',
        quantity: data.selectedQuantity,
        brand: productPageState.collectionData?.brand,
        category_id: productPageState.categoryId,
        collection_id: productPageState.collectionId,
        key_item_color: productPageState.collectionData?.activeProduct?.kicId,
        long_sku: productPageState.collectionData?.activeProduct?.storeItemNumber,
        short_sku: productPageState.collectionData?.qualifiedSku,
        price_flag: productPageState.collectionData?.activeProduct?.priceFlag,
        price_list_usd: data.atbResponseData.listPriceUSD,
        price_offer_usd: data.atbResponseData.offerPriceUSD,
        product_id: productPageState.collectionData?.activeProduct?.productId,
        product_rating: productPageState.reviewData.averageOverallRating || 'null',
        price_list_local: priceToDigits(productPageState.collectionData?.activeProduct?.prices.list.originalPrice),
        price_offer_local: priceToDigits(productPageState.collectionData?.activeProduct?.prices.list.discountPrice),
        category_name: parseBreadcrumbsToCategoryName(productPageState.collectionData?.breadcrumbsUrls),
        inventory: getProductMessage(productPageState.collectionData?.showUrgencyMessage),
        short_descriptor: productPageState.collectionData?.activeProduct?.shortDescription,
        social_proofing: productPageState.collectionData?.hasSocialProofing ?? false,
      });
      window.digitalData.set('addToBag.stickyAtb', false);
    }
  }

  static soldOutSizeTileClick(productPageState, productSizeInfo) {
    if (typeof window === 'undefined') return;
    window.analytics.triggerEvent({
      ...mapBaseProductDataToTealiumVariables(productPageState),
      product_size_info: productSizeInfo,
      event_name: 'sold_out_size_click',
      data_text: 'sold_out_size',
      data_action: 'click',
    }, soldOutSizeTileSchema);
  }

  static universalScrollAction(dataAction) {
    if (typeof window === 'undefined') return;
    window.digitalData.trigger('universal_scroll', {
      Tealium_event: 'scroll',
      data_text: 'user scrolled to',
      data_action: dataAction,
    });
  }

  static universalClick(dataAction, dataText, hasGoldenStateAnalytics, productPageState) {
    if (typeof window === 'undefined') return;

    if (hasGoldenStateAnalytics) {
      window.analytics.triggerEvent({
        ...mapBaseProductDataToTealiumVariables(productPageState),
        event_name: 'click',
        data_action: dataAction,
        data_text: dataText,
      }, universalClickSchema);
    } else {
      oldUniversalClick(dataAction, dataText);
    }
  }

  static productNotificationSuccess(productPageState, {
    email, primarySizeValue, secondarySizeValue, productSizeInfo,
  }) {
    if (typeof window === 'undefined') return;

    const analyticsData = {
      ...mapBaseProductDataToTealiumVariables(productPageState),
      event_name: 'product_notification_success',
      tealium_event: 'product_notification_success',
      data_text: 'product_notification_success',
      data_action: 'click',
      product_notification_email_hash: email || 'null',
      primary_size_value: primarySizeValue || 'null',
      secondary_size_value: secondarySizeValue || 'null',
      product_size_info: productSizeInfo || 'null',
      primary_category_id: productPageState.collectionData?.primaryCategoryId.toString() || 'null',
      short_sku: productPageState.collectionData?.soldOutSku || 'null',
    };

    window.analytics.triggerEvent(
      analyticsData,
      productNotificationSuccessSchema,
    );
  }
}
